/**
 * Cycle Workflow Behavior API Service
 * =====================================
 *
 * Wraps the CycleRuleEngine backend endpoints.
 *
 * HARD-STOP: This service fetches behaviour configuration only.
 * No financial calculations must be performed here or by callers.
 */

import { apiClient } from '@/modules/shared/services/apiClient';
import { ApiResponse } from '@/modules/shared/types';
import type {
  CycleBehaviorResult,
  CycleInvoiceDuplicateCheckResult,
} from '../types/cycleWorkflowBehavior';

const BASE = (programId: string) => `/programs/${programId}`;

export const cycleWorkflowBehaviorService = {
  /**
   * Returns the resolved workflow behaviour rules for a specific cycle number.
   *
   * Drives frontend decisions on:
   *   - Field visibility (monetary / reimbursement)
   *   - Invoice upload requirement
   *   - FOC eligibility transition display
   */
  getCycleBehavior: async (
    programId: string,
    cycleNumber: number,
  ): Promise<CycleBehaviorResult> => {
    const response = await apiClient.get<ApiResponse<CycleBehaviorResult>>(
      `${BASE(programId)}/cycles/${cycleNumber}/workflow-behavior`,
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Failed to load cycle workflow behaviour');
    }
    return response.data.data;
  },

  /**
   * Checks whether an invoice has already been submitted for this cycle.
   * Must be called before allowing a new invoice upload to prevent duplicates.
   */
  checkDuplicateCycleInvoice: async (
    programId: string,
    caseId: string,
    cycleNumber: number,
  ): Promise<CycleInvoiceDuplicateCheckResult> => {
    const response = await apiClient.get<ApiResponse<CycleInvoiceDuplicateCheckResult>>(
      `${BASE(programId)}/cases/${caseId}/cycle-invoices/${cycleNumber}/duplicate-check`,
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Failed to check for duplicate cycle invoice');
    }
    return response.data.data;
  },
};
